import React, { useState, useEffect, useRef } from 'react';

import { useResizeObserver } from 'usehooks-ts';

import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Input, Table, Tag, Select, Tooltip, Flex, Button, Popconfirm, DatePicker } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { NotificationInstance } from 'antd/es/notification/interface';

import {
    LoadingOutlined,
    ReloadOutlined,
    DeleteFilled,
    EditFilled,
    CheckCircleFilled,
} from '@ant-design/icons';

import LoadingPlan from '@flc/components/logistics/import/preloading/loading-plan';
import IBaseComponentProps from '@components/base-component-props';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import { exception } from '@extensions/notification';
import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { delayAction, getDate, getEnumList } from '@extensions/utils';

import { ILoadingPlanFilter } from '@entities/loading-plan-filter';
import { ILoadingPlan } from '@entities/loading-plan';
import { ICountry } from '@entities/country';
import { IUserSession } from '@entities/user-session';
import { IUser } from '@entities/user';

import { LoadingPlanStatus, enumLabel as statusLabel } from '@enums/loading-plan-status';
import { IEnumItem } from '@enums/enum-item';
import { UserType } from '@enums/user-type';

dayjs.extend(utc);

const filterContext: string = 'LoadingPlans';

interface ILoadingPlansProps extends IBaseComponentProps {
    countries: Array<ICountry>;
    api: NotificationInstance;
}

const LoadingPlans = (props: ILoadingPlansProps) => {
    const { countries, api, serverFetch } = props;

    const { RangePicker } = DatePicker;

    const initFilter: ILoadingPlanFilter = {
        statuses: [LoadingPlanStatus.New, LoadingPlanStatus.InProcess],
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const filter = useAppSelector<ILoadingPlanFilter>((s) => s.filters[filterContext]);
    const [contractors, setContractors] = useState<Array<IUser>>([]);
    const [plans, setPlans] = useState<Array<ILoadingPlan>>();
    const [currentPlan, setCurrentPlan] = useState<ILoadingPlan>();

    const [loading, setLoading] = useState<boolean>(false);
    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [openPlan, setOpenPlan] = useState<boolean>(false);

    const [statuses] = useState<Array<IEnumItem>>(getEnumList(LoadingPlanStatus, statusLabel));

    useEffect(() => {
        if (!refreshRequired) return;

        let cleanup = false;
        setLoading(true);

        const fetchData = async () => {
            const promises = [
                await serverFetch(`loadingplans`, { method: 'GET', queryParams: filter })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения плана погрузок', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('users', { method: 'GET', queryParams: { types: [UserType.Supplier] /* isActiveOnly: true  */ } })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения поставщиков', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setPlans(result[0][0]);
                setContractors(result[0][1]);

                setCurrentPlan(undefined);
                setRefreshRequired(false);

                setLoading(false);
            });
        };

        fetchData();
        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));
            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const onDelete = (id: string | undefined) => {
        if (!id) return;

        serverFetch(`loadingplans/${id}`, { method: 'DELETE' })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления плана погрузки', ex, () => d(userLoaded(undefined)));
            });
    };

    const onComplete = (data: ILoadingPlan | undefined) => {
        if (!data) return;

        serverFetch(`loadingplans/complete`, { method: 'POST', bodyData: data })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка одобрения плана', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            key='consigneeCode'
                            style={{ width: 130 }}
                            placeholder='ID клиента'
                            value={filter?.consigneeCode}
                            onChange={(data) => {
                                d(setFilter({ ...filter, consigneeCode: data.target.value }, filterContext));
                            }}
                        />,
                        <Input
                            key='markingCode'
                            style={{ width: 130 }}
                            placeholder='Маркировка'
                            value={filter?.markingCode}
                            onChange={(data) => {
                                d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='contractor'
                            placeholder='Поставщик'
                            value={filter?.contractorId}
                            size='middle'
                            allowClear={true}
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, contractorId: value }, filterContext))}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={contractors.map((c) => {
                                return { value: c.id, label: c.login };
                            })}
                        />,
                        <Select
                            key='status'
                            placeholder='Статус'
                            value={filter?.statuses}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 230 }}
                            onChange={(value) => d(setFilter({ ...filter, statuses: value }, filterContext))}
                            options={statuses.map((s) => {
                                return { value: s.value, label: s.label };
                            })}
                        />,
                        <Select
                            key='country'
                            placeholder='Страна'
                            value={filter?.countryId}
                            size='middle'
                            allowClear={true}
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, countryId: value }, filterContext))}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={countries.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />,
                        <RangePicker
                            style={{ width: 220 }}
                            allowEmpty={[true, true]}
                            key='departure'
                            format='DD.MM.YYYY'
                            placeholder={['Погрузка с', 'до']}
                            cellRender={(current) => {
                                return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                            }}
                            value={[
                                filter?.plannedFrom ? dayjs(filter?.plannedFrom) : null,
                                filter?.plannedTo ? dayjs(filter?.plannedTo) : null,
                            ]}
                            onChange={(value) => {
                                if (!value) {
                                    d(
                                        setFilter(
                                            {
                                                ...filter,
                                                plannedFrom: undefined,
                                                plannedTo: undefined,
                                            },
                                            filterContext,
                                        ),
                                    );

                                    return;
                                }

                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            plannedFrom: value[0]
                                                ? dayjs(value[0]).utc(true).startOf('day').toString()
                                                : undefined,
                                            plannedTo: value[1]
                                                ? dayjs(value[1]).utc(true).endOf('day').toString()
                                                : undefined,
                                        },
                                        filterContext,
                                    ),
                                );
                            }}
                        />,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<ILoadingPlan> = [
            {
                title: '',
                width: 60,
                onCell: (record) => ({
                    style: {
                        background: record.status == LoadingPlanStatus.New ? '#fbe7eb' : '',
                    },
                }),
                render: (_, record) => {
                    return (
                        <Flex>
                            <Tooltip title='Обработать план погрузки'>
                                <Button
                                    icon={<EditFilled />}
                                    variant='link'
                                    color='blue'
                                    onClick={() => {
                                        setCurrentPlan(record);
                                        setOpenPlan(true);
                                    }}
                                />
                            </Tooltip>
                            <Popconfirm title='Удалить выбранный план погрузки?' onConfirm={() => onDelete(record.id)}>
                                <Tooltip title='Удалить план погрузки'>
                                    <Button icon={<DeleteFilled />} variant='link' color='blue' />
                                </Tooltip>
                            </Popconfirm>
                        </Flex>
                    );
                },
            },
            {
                title: '',
                width: 40,
                align: 'center',
                onCell: (record) => ({
                    style: {
                        background: record.status == LoadingPlanStatus.New ? '#fbe7eb' : '',
                    },
                }),
                render: (_, record) => {
                    if (record.status == LoadingPlanStatus.New) {
                        return (
                            <Tooltip placement='top' title='План погрузки не проведен' color='var(--main-red)'>
                                <CheckCircleFilled
                                    style={{
                                        color: 'var(--main-red)',
                                        fontSize: 18,
                                        marginTop: 5,
                                    }}
                                />
                            </Tooltip>
                        );
                    }

                    if (record.status == LoadingPlanStatus.Processed) {
                        return (
                            <Tooltip placement='top' title='План погрузки проведен' color='var(--main-green)'>
                                <CheckCircleFilled
                                    style={{
                                        color: 'var(--main-green)',
                                        fontSize: 18,
                                        marginTop: 5,
                                    }}
                                />
                            </Tooltip>
                        );
                    }

                    return (
                        <Tooltip placement='top' title='План погрузки в работе' color='#bfbfbf'>
                            <CheckCircleFilled
                                style={{
                                    color: '#bfbfbf',
                                    fontSize: 18,
                                    marginTop: 5,
                                }}
                            />
                        </Tooltip>
                    );
                },
            },
            {
                title: 'ID',
                width: 80,
                dataIndex: 'consigneeCode',
                onCell: (record) => ({
                    style: {
                        background: record.status == LoadingPlanStatus.New ? '#fbe7eb' : '',
                        fontWeight: record.status == LoadingPlanStatus.New ? '600' : '',
                    },
                }),
            },
            {
                title: 'Маркировка',
                width: 200,
                dataIndex: 'markingCode',
                onCell: (record) => ({
                    style: {
                        background: record.status == LoadingPlanStatus.New ? '#fbe7eb' : '',
                        fontWeight: record.status == LoadingPlanStatus.New ? '600' : '',
                    },
                }),
            },
            {
                title: 'Страна',
                width: 110,
                dataIndex: 'countryName',
                onCell: (record) => ({
                    style: {
                        background: record.status == LoadingPlanStatus.New ? '#fbe7eb' : '',
                        fontWeight: record.status == LoadingPlanStatus.New ? '600' : '',
                    },
                }),
            },
            {
                title: 'Продукт',
                width: 190,
                align: 'center',
                onCell: (record) => ({
                    style: {
                        background: record.status == LoadingPlanStatus.New ? '#fbe7eb' : '',
                    },
                }),
                render: (_, record) => {
                    return (
                        <Tag
                            color={record.itemColor ?? 'red'}
                            style={{
                                marginBottom: 2,
                                paddingBottom: 2,
                                width: 'fit-content',
                                fontSize: 15,
                                fontWeight: 600,
                            }}
                        >
                            {record.itemName ?? '- Не задан -'}
                        </Tag>
                    );
                },
            },
            {
                title: 'AWB / Торговая площадка',
                align: 'center',
                dataIndex: 'awbNumber',
                width: 200,
                onCell: (record) => ({
                    style: {
                        background: record.status == LoadingPlanStatus.New ? '#fbe7eb' : '',
                        fontWeight: record.status == LoadingPlanStatus.New ? '600' : '',
                    },
                }),
            },
            {
                title: 'Кол-во / шт',
                align: 'center',
                dataIndex: 'qty',
                width: 120,
                onCell: (record) => ({
                    style: {
                        background: record.status == LoadingPlanStatus.New ? '#fbe7eb' : '#fafafa',
                        fontWeight: 600,
                    },
                }),
            },
            {
                title: 'Брутто / кг',
                align: 'center',
                dataIndex: 'grossWeight',
                width: 120,
                onCell: (record) => ({
                    style: {
                        background: record.status == LoadingPlanStatus.New ? '#fbe7eb' : '#fafafa',
                        fontWeight: 600,
                    },
                }),
            },
            {
                title: 'Объемный / кг',
                align: 'center',
                dataIndex: 'volumeWeight',
                width: 120,
                onCell: (record) => ({
                    style: {
                        background: record.status == LoadingPlanStatus.New ? '#fbe7eb' : '#fafafa',
                        fontWeight: 600,
                    },
                }),
            },
            {
                title: 'Планируемая дата',
                width: 140,
                align: 'center',
                onCell: (record) => ({
                    style: {
                        background: record.status == LoadingPlanStatus.New ? '#fbe7eb' : '',
                        fontWeight: record.status == LoadingPlanStatus.New ? '600' : '',
                    },
                }),
                render: (_, record) => {
                    return getDate(record.plannedDate);
                },
            },
            {
                title: 'Телефон',
                dataIndex: 'userPhone',
                width: 170,
                onCell: (record) => ({
                    style: {
                        background: record.status == LoadingPlanStatus.New ? '#fbe7eb' : '',
                        fontWeight: record.status == LoadingPlanStatus.New ? '600' : '',
                    },
                }),
            },
            {
                title: 'ФИО',
                width: 300,
                dataIndex: 'fullName',
                onCell: (record) => ({
                    style: {
                        background: record.status == LoadingPlanStatus.New ? '#fbe7eb' : '',
                        fontWeight: record.status == LoadingPlanStatus.New ? '600' : '',
                    },
                }),
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={plans}
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                pagination={false}
                scroll={{ y: height, x: 'max-content' }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 295px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {openPlan && (
                <LoadingPlan
                    planId={currentPlan?.id}
                    baseCountries={countries}
                    userSession={userSession}
                    api={api}
                    onSave={() => {
                        setOpenPlan(false);
                        setRefreshRequired(true);
                    }}
                    onDelete={() => {
                        setOpenPlan(false);
                        onDelete(currentPlan?.id);
                    }}
                    onComplete={(plan: ILoadingPlan) => {
                        setOpenPlan(false);
                        onComplete(plan);
                    }}
                    onCancel={() => {
                        setOpenPlan(false);
                    }}
                    serverFetch={serverFetch}
                />
            )}
        </div>
    );
};

export default LoadingPlans;
